// Утилиты доступа: декодируем JWT из localStorage, достаём claim `acc` (CSV
// резолвнутых access-ID из admin-api), возвращаем Set для O(1) проверки.
//
// ВАЖНО: claim `r` несёт ROLE-id ("1","2","3") и нужен бэкенду для checkAccess
// (роли→role_access). Фронт по нему гейтить НЕЛЬЗЯ — role-id ≠ access-id, матч
// никогда не сработает. Поэтому admin-api с какого-то релиза кладёт отдельный
// claim `acc` = резолвнутые access-id (Login + TokenRefresh). Читаем ТОЛЬКО его.
//
// Старый токен (до редеплоя admin-api) не содержит `acc` → набор пустой →
// hasAccess fail-open (см. ниже) показывает всё. После редеплоя + перелогина
// `acc` появляется → корректный гейтинг. На `r` НЕ фолбэчим намеренно.
//
// Раньше sidebar показывал ВСЕ пункты независимо от ролей — оператор без
// projectList видел «Проекты», открывал страницу и получал 403 от бэка.
// Бэкенд защищает данные (checkAccess), но утечка структуры админки была.

let cachedToken: string | null = null;
let cachedAccesses: Set<string> = new Set();

function base64UrlDecode(s: string): string {
  // base64url → base64 (+ паддинг до кратности 4).
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
  return atob(b64 + pad);
}

/**
 * Возвращает Set access-IDs текущего оператора из JWT (claim `r`, CSV).
 *
 * Кеширует по строке токена — пока токен не сменился, не парсим заново.
 * Без токена / битый payload → пустой Set (fail-closed: гейтированные
 * пункты меню НЕ показываем). Это безопасный дефолт; navbar рендерится
 * только в авторизованной зоне (PrivateRoute), так что в норме токен есть.
 */
export function getUserAccesses(): Set<string> {
  const token = localStorage.getItem('token');
  if (!token) {
    cachedToken = null;
    cachedAccesses = new Set();
    return cachedAccesses;
  }
  if (token === cachedToken) {
    return cachedAccesses;
  }
  cachedToken = token;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      cachedAccesses = new Set();
      return cachedAccesses;
    }
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    const acc = payload?.acc;
    if (typeof acc !== 'string' || acc.length === 0) {
      // Нет claim `acc` (старый токен / не резолвилось) → пустой набор →
      // hasAccess fail-open покажет всё. На `r` (role-id) НЕ фолбэчим.
      cachedAccesses = new Set();
      return cachedAccesses;
    }
    cachedAccesses = new Set(acc.split(',').map(s => s.trim()).filter(Boolean));
  } catch {
    cachedAccesses = new Set();
  }
  return cachedAccesses;
}

/**
 * Проверка одного access-ID.
 * Пустой/undefined `required` → доступ свободный (always-visible пункты меню).
 *
 * v60.2 (2026-06-01): fail-OPEN если getUserAccesses() пустой. Раньше при
 * битом/отсутствующем JWT claim `r` все гейтированные пункты меню отваливались
 * (видно было только Диаграммы / Поддержка / Ченджлог) — оператор не мог
 * работать. Backend всё равно защищает данные через `checkAccess` на каждом
 * endpoint'е, так что утечка структуры меню при corrupted token — меньшее
 * зло, чем оператор без навигации. console.warn раз даёт диагностический
 * сигнал в DevTools если что-то с JWT не так.
 */
let warnedAboutEmpty = false;
export function hasAccess(required: string | null | undefined): boolean {
  if (!required) return true;
  const set = getUserAccesses();
  if (set.size === 0) {
    if (!warnedAboutEmpty && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(
        '[access] JWT claim `acc` пустой — sidebar показывает ВСЕ пункты (fail-open). ' +
        'Backend всё ещё защищает данные. Нужен редеплой admin-api (claim acc) + перелогин.'
      );
      warnedAboutEmpty = true;
    }
    return true;
  }
  return set.has(required);
}

/**
 * Проект «по умолчанию» оператора из JWT claim `pid` (#4). Бэкенд кладёт его
 * в токен при Login/Refresh из profiles.default_project_id. null если не задан.
 * Позволяет восстановить выбор проекта сразу из токена, не теряя при простое.
 */
export function getDefaultProjectId(): string | null {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    const pid = payload?.pid;
    return typeof pid === 'string' && pid.length > 0 ? pid : null;
  } catch {
    return null;
  }
}

/**
 * Флаг обязательной смены пароля оператором из JWT claim `mcp` (bool).
 * Бэкенд кладёт его при Login/Refresh из profiles.must_change_password (после
 * reset/temp от админа). true → фронт показывает блокирующий экран смены пароля.
 * Снимается, когда оператор сменит пароль (новый токен после перелогина без mcp).
 */
export function getMustChangePassword(): boolean {
  const token = localStorage.getItem('token');
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    return payload?.mcp === true;
  } catch {
    return false;
  }
}

/**
 * profileId текущего оператора из JWT claim `id`. Возвращает null если
 * токена нет или claim не UUID (логин-страница, истёкший токен).
 */
export function getCurrentProfileId(): string | null {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    const id = payload?.id;
    return typeof id === 'string' && id.length > 0 ? id : null;
  } catch {
    return null;
  }
}
